import { PublicClientApplication } from "@azure/msal-browser";
import { iam_ip } from "./ip_details";
import { getCompleteEndpoint, getProtocol } from "./common_utils";

let publicConfigPromise = null;
let msalInstancePromise = null;

function buildIamEndpoint(path) {
    const protocol = getProtocol(iam_ip);
    return getCompleteEndpoint(protocol, iam_ip, path);
}

function getRedirectResponsePayload() {
    if (window.location.hash && window.location.hash.length > 1) {
        return window.location.hash;
    }

    if (window.location.search && window.location.search.length > 1) {
        return "#" + window.location.search.substring(1);
    }

    return null;
}

async function readJsonResponse(response, fallbackMessage) {
    let responseData = {};
    try {
        responseData = await response.json();
    } catch (error) {
        throw new Error(fallbackMessage);
    }

    return responseData;
}

export async function getMsiPublicConfig() {
    if (publicConfigPromise !== null) {
        return publicConfigPromise;
    }

    publicConfigPromise = fetch(buildIamEndpoint("msi/public_config"), {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            "api-type": "qualdo_db_auth"
        }
    }).then(async (response) => {
        const responseData = await readJsonResponse(response, "Could not load Azure AD configuration.");
        if (responseData.code !== 200) {
            throw new Error(responseData.message || "Could not load Azure AD configuration.");
        }

        const ssoConfig = responseData.response || {};
        if (!ssoConfig.client_id || !ssoConfig.scope || !ssoConfig.authority) {
            throw new Error("Azure AD configuration is incomplete.");
        }

        return ssoConfig;
    }).catch((error) => {
        publicConfigPromise = null;
        throw error;
    });

    return publicConfigPromise;
}

async function getMsalInstance() {
    if (msalInstancePromise !== null) {
        return msalInstancePromise;
    }

    msalInstancePromise = getMsiPublicConfig().then((ssoConfig) => {
        return new PublicClientApplication({
            auth: {
                clientId: ssoConfig.client_id,
                authority: ssoConfig.authority,
                redirectUri: buildIamEndpoint("getAToken"),
                navigateToLoginRequestUrl: false
            },
            cache: {
                cacheLocation: "sessionStorage"
            }
        });
    }).catch((error) => {
        msalInstancePromise = null;
        throw error;
    });

    return msalInstancePromise;
}

async function callMsiTokenLogin(accessToken) {
    const response = await fetch(buildIamEndpoint("msi/token_login"), {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "api-type": "qualdo_db_auth",
            "Authorization": "Bearer " + accessToken
        }
    });
    const responseData = await readJsonResponse(response, "Unexpected response from Qualdo IAM.");
    if (responseData.code !== 200 || !responseData.response || !responseData.response.redirect_url) {
        throw new Error(responseData.message || "Microsoft login failed");
    }

    return responseData.response.redirect_url;
}

function getPreferredAccount(msalInstance, redirectResult) {
    if (redirectResult && redirectResult.account) {
        return redirectResult.account;
    }

    const accounts = msalInstance.getAllAccounts();
    if (accounts && accounts.length > 0) {
        return accounts[0];
    }

    return null;
}

export async function startMsiRedirectLogin() {
    const [msalInstance, ssoConfig] = await Promise.all([
        getMsalInstance(),
        getMsiPublicConfig()
    ]);

    await msalInstance.loginRedirect({
        scopes: [ssoConfig.scope]
    });
}

export async function completeMsiRedirectLogin() {
    const [msalInstance, ssoConfig] = await Promise.all([
        getMsalInstance(),
        getMsiPublicConfig()
    ]);

    const redirectResponsePayload = getRedirectResponsePayload();

    const redirectResult = await msalInstance.handleRedirectPromise(redirectResponsePayload);

    const account = getPreferredAccount(msalInstance, redirectResult);

    if (!account) {
        return null;
    }

    let tokenResponse = redirectResult;
    if (!tokenResponse || !tokenResponse.accessToken) {
        tokenResponse = await msalInstance.acquireTokenSilent({
            scopes: [ssoConfig.scope],
            account: account
        });
    }

    const redirectUrl = await callMsiTokenLogin(tokenResponse.accessToken);

    return {
        redirectUrl,
        account,
        tokenResponse
    };
}
